import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  CalendarBlank, 
  CheckCircle, 
  Circle,
  PencilSimple,
  Eye,
  Clock,
  ArrowLeft,
  Plus,
  CaretDown,
  CaretRight
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// 12-Week Content Calendar from ChatGPT transcript
const CONTENT_CALENDAR = [
  {
    month: "Month 1",
    theme: "Build Authority (Global + Big Themes)",
    weeks: [
      {
        week: 1,
        title: "Who Really Controls the World's Food Supply?",
        category: "Global Agriculture",
        status: "published",
        seoKeywords: ["global food supply", "agriculture control", "food security"],
        socialHook: "A small number of countries control most of the world's food exports. Here's who they are."
      },
      {
        week: 2,
        title: "Why Brazil Has Become the World's Farming Superpower",
        category: "Global Agriculture",
        status: "published",
        seoKeywords: ["Brazil agriculture", "soybean exports", "farming superpower"],
        socialHook: "Brazil now dominates global soybean exports. How did a frontier become a powerhouse?"
      },
      {
        week: 3,
        title: "How Russia Became the World's Wheat King",
        category: "Global Agriculture",
        status: "published",
        seoKeywords: ["Russia wheat", "Black Sea exports", "wheat king"],
        socialHook: "Russia went from grain importer to the world's #1 wheat exporter. Here's the story."
      },
      {
        week: 4,
        title: "Is Food the New Oil?",
        category: "Analysis",
        status: "published",
        seoKeywords: ["food security", "geopolitics agriculture", "strategic food"],
        socialHook: "Food exports are becoming strategic assets. Agriculture is the new geopolitics."
      }
    ]
  },
  {
    month: "Month 2",
    theme: "Educate (Cycles + Economics)",
    weeks: [
      {
        week: 5,
        title: "What Happens to Farming in a Downturn",
        category: "Markets",
        status: "published",
        seoKeywords: ["farming recession", "agriculture downturn", "farm cycle"],
        socialHook: "Fertiliser falls first, then farm income, then machinery. The agriculture cycle explained."
      },
      {
        week: 6,
        title: "Why Farmland Prices Behave Differently to Agri Stocks",
        category: "Markets",
        status: "published",
        seoKeywords: ["farmland prices", "agriculture stocks", "land investment"],
        socialHook: "Farmland moves slowly. Stocks move fast. Why the difference matters."
      },
      {
        week: 7,
        title: "The Hidden Driver of Farm Profit: Fertiliser Prices",
        category: "Markets",
        status: "published",
        seoKeywords: ["fertiliser prices", "farm margins", "input costs"],
        socialHook: "Fertiliser is the first signal in the agriculture cycle. Everything else follows."
      },
      {
        week: 8,
        title: "Why Machinery Sales Signal the Health of Farming",
        category: "Markets",
        status: "published",
        seoKeywords: ["farm machinery", "tractor sales", "agriculture indicators"],
        socialHook: "When farmers stop buying equipment, the sector is in trouble."
      }
    ]
  },
  {
    month: "Month 3",
    theme: "Local + Practical (Ireland Focus)",
    weeks: [
      {
        week: 9,
        title: "Why Irish Farming Is Built Differently",
        category: "Ireland",
        status: "published",
        seoKeywords: ["Irish farming", "grass-fed", "family farms Ireland"],
        socialHook: "Ireland's farming model is unique. Here's why it matters."
      },
      {
        week: 10,
        title: "The Irish Companies Powering Global Agriculture",
        category: "Ireland",
        status: "published",
        seoKeywords: ["Kerry Group", "Glanbia", "Origin Enterprises", "Irish agri companies"],
        socialHook: "Origin, Kerry, Glanbia: Irish companies quietly shaping global food systems."
      },
      {
        week: 11,
        title: "The Future of Dairy in Ireland: Opportunity vs Pressure",
        category: "Ireland",
        status: "published",
        seoKeywords: ["Irish dairy", "milk prices", "dairy future Ireland"],
        socialHook: "Irish dairy faces both opportunity and pressure. What does the future hold?"
      },
      {
        week: 12,
        title: "What Would Happen If Fertiliser Supply Collapsed?",
        category: "Analysis",
        status: "published",
        seoKeywords: ["fertiliser crisis", "food supply collapse", "agriculture risk"],
        socialHook: "A scenario-based look at one of agriculture's biggest vulnerabilities."
      }
    ]
  }
];

// Status badge component
function StatusBadge({ status }) {
  const styles = {
    published: "bg-[#ECFDF5] text-[#059669] border-[#A7F3D0]",
    drafted: "bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]",
    planned: "bg-[#F6E9DD] text-[#6B6661] border-[#E0D4C8]"
  };

  const icons = {
    published: <CheckCircle size={14} weight="fill" />,
    drafted: <PencilSimple size={14} />,
    planned: <Circle size={14} />
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium border rounded-full ${styles[status]}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// Category badge
function CategoryBadge({ category }) {
  const colors = {
    "Global Agriculture": "bg-[#0F766E] text-white",
    "Analysis": "bg-[#7C3AED] text-white",
    "Markets": "bg-[#2563EB] text-white",
    "Ireland": "bg-[#059669] text-white"
  };

  return (
    <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${colors[category] || "bg-gray-500 text-white"}`}>
      {category}
    </span>
  );
}

export default function ContentCalendarPage() {
  const { user } = useAuth();
  const [expandedMonths, setExpandedMonths] = useState({ "Month 1": true, "Month 2": true, "Month 3": true });
  const [publishedArticles, setPublishedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPublishedArticles();
  }, []);

  const fetchPublishedArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles/featured?limit=20`);
      setPublishedArticles(response.data.map(a => a.title));
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (month) => {
    setExpandedMonths(prev => ({
      ...prev,
      [month]: !prev[month]
    }));
  };

  // Check if article is published based on title match
  const getArticleStatus = (title) => {
    // Normalize titles for comparison
    const normalizedTitle = title.toLowerCase().replace(/[^a-z0-9]/g, '');
    const isPublished = publishedArticles.some(pubTitle => {
      const normalizedPubTitle = pubTitle.toLowerCase().replace(/[^a-z0-9]/g, '');
      // Check if the calendar title is contained in the published title or vice versa
      return normalizedPubTitle.includes(normalizedTitle.slice(0, 20)) || 
             normalizedTitle.includes(normalizedPubTitle.slice(0, 20));
    });
    return isPublished ? "published" : "planned";
  };

  // Stats
  const totalArticles = CONTENT_CALENDAR.reduce((acc, m) => acc + m.weeks.length, 0);
  const publishedCount = CONTENT_CALENDAR.reduce((acc, m) => 
    acc + m.weeks.filter(w => getArticleStatus(w.title) === "published").length, 0
  );

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />
      
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
        {/* Back Link */}
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-[#6B6661] hover:text-[#0F766E] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm">Back to Admin</span>
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <CalendarBlank size={28} className="text-[#0F766E]" weight="duotone" />
              <h1 className="font-serif text-3xl text-[#33302E]">Content Calendar</h1>
            </div>
            <p className="text-[#6B6661]">12-week editorial plan for Farmwatch</p>
          </div>
          
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="create-article-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#064E3B] transition-colors"
            >
              <Plus size={18} />
              Create Article
            </Link>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-[#E0D4C8] p-4">
            <div className="text-2xl font-serif text-[#33302E]">{totalArticles}</div>
            <div className="text-sm text-[#6B6661]">Total Planned</div>
          </div>
          <div className="bg-white border border-[#E0D4C8] p-4">
            <div className="text-2xl font-serif text-[#059669]">{publishedCount}</div>
            <div className="text-sm text-[#6B6661]">Published</div>
          </div>
          <div className="bg-white border border-[#E0D4C8] p-4">
            <div className="text-2xl font-serif text-[#6B6661]">{totalArticles - publishedCount}</div>
            <div className="text-sm text-[#6B6661]">Remaining</div>
          </div>
          <div className="bg-white border border-[#E0D4C8] p-4">
            <div className="text-2xl font-serif text-[#0F766E]">{Math.round((publishedCount / totalArticles) * 100)}%</div>
            <div className="text-sm text-[#6B6661]">Complete</div>
          </div>
        </div>

        {/* Calendar */}
        <div className="space-y-6">
          {CONTENT_CALENDAR.map((month) => (
            <div key={month.month} className="bg-white border border-[#E0D4C8]">
              {/* Month Header */}
              <button
                onClick={() => toggleMonth(month.month)}
                className="w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#022C22] to-[#064E3B] text-white hover:from-[#064E3B] hover:to-[#0F766E] transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedMonths[month.month] ? <CaretDown size={20} /> : <CaretRight size={20} />}
                  <div className="text-left">
                    <h2 className="font-serif text-xl">{month.month}</h2>
                    <p className="text-sm text-[#A7F3D0]">{month.theme}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-sm text-[#6EE7B7]">
                    {month.weeks.filter(w => getArticleStatus(w.title) === "published").length}/{month.weeks.length} published
                  </span>
                </div>
              </button>

              {/* Weeks */}
              {expandedMonths[month.month] && (
                <div className="divide-y divide-[#E5E7EB]">
                  {month.weeks.map((week) => {
                    const actualStatus = getArticleStatus(week.title);
                    return (
                      <div key={week.week} className="p-6 hover:bg-[#F9FAFB] transition-colors">
                        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                          {/* Week Number */}
                          <div className="flex-shrink-0 w-16">
                            <div className="w-12 h-12 flex items-center justify-center bg-[#F6E9DD] border border-[#E0D4C8] rounded-full">
                              <span className="text-sm font-semibold text-[#6B6661]">W{week.week}</span>
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <CategoryBadge category={week.category} />
                              <StatusBadge status={actualStatus} />
                            </div>
                            <h3 className="font-serif text-lg text-[#33302E] mb-2">{week.title}</h3>
                            
                            {/* Social Hook */}
                            <p className="text-sm text-[#6B6661] mb-3 italic">
                              "{week.socialHook}"
                            </p>

                            {/* SEO Keywords */}
                            <div className="flex flex-wrap gap-2">
                              {week.seoKeywords.map((keyword) => (
                                <span 
                                  key={keyword}
                                  className="px-2 py-0.5 bg-[#F6E9DD] text-[#6B6661] text-xs rounded"
                                >
                                  {keyword}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            {actualStatus === "published" ? (
                              <span className="text-xs text-[#059669] flex items-center gap-1">
                                <Eye size={14} />
                                Live
                              </span>
                            ) : (
                              <span className="text-xs text-[#6B6661] flex items-center gap-1">
                                <Clock size={14} />
                                Pending
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Writing Guidelines */}
        <div className="mt-12 bg-[#F9FAFB] border border-[#E0D4C8] p-8">
          <h2 className="font-serif text-2xl text-[#33302E] mb-4">Editorial Guidelines</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold text-[#33302E] mb-3">Writing Style</h3>
              <ul className="space-y-2 text-sm text-[#6B6661]">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  Clear, authoritative tone
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  No fluff - every sentence adds value
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  Short paragraphs (2-4 sentences)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  Avoid jargon unless explained
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-[#33302E] mb-3">Content Multiplier</h3>
              <ul className="space-y-2 text-sm text-[#6B6661]">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  1 full article → website
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  3 LinkedIn posts → social
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  1 short "insight" post → engagement
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#0F766E] mt-0.5 flex-shrink-0" />
                  1 email newsletter → subscribers
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
